/**
 * The table tab's WHERE box, split the way DBeaver's filter box reads it
 * (owner, 30.09.2026: `uid > 1000 ORDER BY uid DESC` failed with a 1064 on
 * MariaDB because the text landed between the grid's own clauses and made
 * two ORDER BYs).
 *
 *   "uid > 1000 ORDER BY uid DESC"  -> { where: "uid > 1000", orderBy: "uid DESC" }
 *   "ORDER BY created_at"           -> { where: "",           orderBy: "created_at" }
 *   "status = 'x' LIMIT 50"         -> { where: "status = 'x'", orderBy: "", limitDropped: true }
 *
 * Only a TOP-LEVEL `ORDER BY` / `LIMIT` counts: inside a string literal, a
 * quoted identifier, a comment or parentheses (a subquery) they are left
 * alone. A trailing LIMIT is dropped because the grid pages with its own
 * LIMIT/OFFSET — DBeaver ignores it the same way.
 *
 * Nothing here makes the text safer or less safe: both parts are still
 * concatenated into ONE statement sent over the prepared single-statement
 * protocol, so `1=1; DROP TABLE t` is rejected by the driver exactly as
 * before (see normalizeWhereFragment).
 */
import { normalizeWhereFragment } from './dbGridSql';

/** Start offsets of top-level (depth 0, unquoted, uncommented) words matching `re`. */
function topLevelMatches(text, re) {
  const hits = [];
  let depth = 0;
  let i = 0;
  const n = text.length;
  while (i < n) {
    const c = text[i];
    const next = text[i + 1];
    if (c === "'" || c === '"' || c === '`') {
      // Quoted: doubled quote escapes; a backslash escapes in MySQL strings.
      i += 1;
      while (i < n) {
        if (text[i] === '\\' && c === "'") { i += 2; continue; }
        if (text[i] === c) {
          if (text[i + 1] === c) { i += 2; continue; }
          break;
        }
        i += 1;
      }
      i += 1;
      continue;
    }
    if (c === '[') { // SQL Server / SQLite bracket identifiers
      const end = text.indexOf(']', i + 1);
      i = end === -1 ? n : end + 1;
      continue;
    }
    if (c === '-' && next === '-') {
      const end = text.indexOf('\n', i);
      i = end === -1 ? n : end + 1;
      continue;
    }
    if (c === '/' && next === '*') {
      const end = text.indexOf('*/', i + 2);
      i = end === -1 ? n : end + 2;
      continue;
    }
    if (c === '(') { depth += 1; i += 1; continue; }
    if (c === ')') { depth = Math.max(0, depth - 1); i += 1; continue; }
    if (depth === 0 && /[A-Za-z]/.test(c) && (i === 0 || !/[\w$]/.test(text[i - 1]))) {
      re.lastIndex = i;
      const m = re.exec(text);
      if (m && m.index === i) {
        hits.push({ index: i, length: m[0].length });
        i += m[0].length;
        continue;
      }
    }
    i += 1;
  }
  return hits;
}

const ORDER_BY = /ORDER\s+BY\b/iy;
const LIMIT = /LIMIT\b/iy;

export function splitFilterClause(text) {
  const clean = normalizeWhereFragment(text);
  let where = clean;
  let orderBy = '';
  let limitDropped = false;

  const order = topLevelMatches(clean, ORDER_BY)[0];
  let tail = '';
  if (order) {
    where = clean.slice(0, order.index).trim();
    tail = clean.slice(order.index + order.length);
  }
  const scope = order ? tail : where;
  const limit = topLevelMatches(scope, LIMIT)[0];
  if (limit) {
    limitDropped = true;
    if (order) tail = tail.slice(0, limit.index);
    else where = where.slice(0, limit.index).trim();
  }
  if (order) orderBy = tail.trim();
  return { where, orderBy, limitDropped };
}

/** ` WHERE …` for the grid, count and export queries, or ''. */
export function filterWhereSql(text) {
  const { where } = splitFilterClause(text);
  return where ? ` WHERE ${where}` : '';
}

/**
 * The ORDER BY for a grid page: the column-header sort when one is set
 * (`headerOrder`, from orderByClause), otherwise the box's own ORDER BY.
 * Clearing the header sort hands the order back to the box.
 */
export function gridOrderSql(headerOrder, text) {
  if (headerOrder) return headerOrder;
  const { orderBy } = splitFilterClause(text);
  return orderBy ? ` ORDER BY ${orderBy}` : '';
}

/**
 * The box's text with one more condition ANDed in (the grid's right-click
 * "Filter" items) — before any ORDER BY the box carries, not after it, which
 * would read `… ORDER BY uid DESC AND status = 'x'`. A dropped LIMIT stays
 * dropped.
 */
export function appendFilterCondition(text, condition) {
  const { where, orderBy } = splitFilterClause(text);
  const next = where ? `${where} AND ${condition}` : condition;
  return orderBy ? `${next} ORDER BY ${orderBy}` : next;
}
